/**
 * ProceduresAdmin.tsx — Admin > Products > Procedures.
 *
 * Legacy's Procedures (admin/admin_procedures_list.php): the account's
 * category > sub-category > procedure tree on one board, as Products is laid
 * out (NestedCategoryBoard): categories, their sub-categories inside them,
 * and each sub-category's procedures inside it, every level A–Z. Drag and
 * drop only re-files (2026-10-07, user: order no longer matters): a
 * sub-category onto another category, a procedure onto another sub-category.
 * A search opens everything it matched.
 *
 * Add / edit open a form (ProcedureFormModals); delete and restore work as
 * legacy: off and out of the order, children left as they are (hidden while
 * their parent is). Accounts with imported I&E content also get its editor
 * (IeContentModal). Each change saves on its own; legacy saved the whole tree
 * with Save All.
 *
 * On a Locked page (useAdminPageLevel) nothing changes and nothing says why:
 * no Add, row icons or drag. A procedure still opens from its row, and a
 * category or sub-category (whose row only opens and closes it) from a View
 * icon, since its form holds more than the row shows; every form is read-only.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { AdminBoardPage, AdminListEmpty, AdminListToolbar } from '@/components/admin/AdminList';
import { AdminRowAction } from '@/components/admin/AdminRowActions';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { boardDragHint, type BoardCategory } from '@/components/admin/CategoryBoard';
import { compareTitles } from '@/components/admin/categoryBoardState';
import { IeContentModal } from '@/components/admin/IeContentModal';
import { NestedCategoryBoard } from '@/components/admin/NestedCategoryBoard';
import {
  ProcedureCategoryModal,
  ProcedureModal,
  ProcedureSubcategoryModal,
  type DocumentChoice,
} from '@/components/admin/ProcedureFormModals';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import { getErrorMessage } from '@/services/apiClient';
import { listAdminDocuments, listDocumentCategories } from '@/services/documentsAdmin.service';
import {
  deleteProcedureRow,
  getProcedureTree,
  reorderProcedureRows,
  restoreProcedureRow,
  type AdminProcedure,
  type AdminProcedureCategory,
  type AdminProcedureSubcategory,
  type IeType,
  type ProcedureLevel,
} from '@/services/proceduresAdmin.service';
import { toast } from '@/store/toastStore';
import { cn } from '@/lib/utils';

type Category = Omit<AdminProcedureCategory, 'subcategories'>;
type Subcategory = AdminProcedureSubcategory;

const byOrder = <T extends { order: number; id: number }>(a: T, b: T) => a.order - b.order || a.id - b.id;
const nameOf = (r: { name: string; id: number }, noun: string) => r.name.trim() || `${noun} #${r.id}`;

interface Deleting {
  level: ProcedureLevel;
  id: number;
  name: string;
}

const DELETE_COPY: Record<ProcedureLevel, { noun: string; hidden: string }> = {
  categories: { noun: 'category', hidden: 'Its sub-categories and procedures are hidden while it is deleted.' },
  subcategories: { noun: 'sub-category', hidden: 'Its procedures are hidden while it is deleted. Plans that already use it keep it.' },
  procedures: { noun: 'procedure', hidden: 'Quotes, plans and memberships that already use it keep it.' },
};

export function ProceduresAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [categories, setCategories] = useState<Category[]>([]);
  const [subs, setSubs] = useState<Subcategory[]>([]);
  const [procs, setProcs] = useState<AdminProcedure[]>([]);
  const [maxHours, setMaxHours] = useState(10);
  const [hasIE, setHasIE] = useState(false);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);

  const [categoryModal, setCategoryModal] = useState<{ category: Category | null } | null>(null);
  const [subModal, setSubModal] = useState<{ sub: Subcategory | null; categoryId: number } | null>(null);
  const [procModal, setProcModal] = useState<{ proc: AdminProcedure | null; subcategoryId: number } | null>(null);
  const [ieModal, setIeModal] = useState<{ types: IeType[]; ownerId: number; ownerName: string } | null>(null);
  const [deleting, setDeleting] = useState<Deleting | null>(null);

  const [documents, setDocuments] = useState<DocumentChoice[]>([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsLoaded, setDocumentsLoaded] = useState(false);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const tree = await getProcedureTree();
      setCategories(tree.categories.map(({ subcategories: _s, ...c }) => c));
      setSubs(tree.categories.flatMap((c) => c.subcategories));
      setProcs(tree.categories.flatMap((c) => c.subcategories.flatMap((s) => s.procedures)));
      setMaxHours(tree.maxHours);
      setHasIE(tree.hasIE);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load procedures'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // The sub-category form's document picker: loaded the first time it opens.
  useEffect(() => {
    if (!subModal || documentsLoaded) return;
    setDocumentsLoading(true);
    Promise.all([listAdminDocuments(), listDocumentCategories()])
      .then(([docs, cats]) => {
        const catName = new Map(cats.map((c) => [c.id, c.name]));
        setDocuments(
          docs
            .filter((d) => d.active)
            .map((d) => ({ id: d.id, title: d.title, category: d.categoryId ? catName.get(d.categoryId) ?? '' : '' })),
        );
        setDocumentsLoaded(true);
      })
      .catch((err) => toast.error(getErrorMessage(err, 'The documents could not be loaded.')))
      .finally(() => setDocumentsLoading(false));
  }, [subModal, documentsLoaded]);

  const query = search.trim().toLowerCase();
  const matches = (text: string) => query === '' || text.toLowerCase().includes(query);
  const narrowed = query !== '';

  // Categories A–Z; sub-categories and procedures in their saved order (the
  // board lists them A–Z, and a move saves the new parent's list in this order).
  const sortedCats = useMemo(() => [...categories].sort((a, b) => compareTitles(a.name, b.name) || a.id - b.id), [categories]);
  const catById = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);
  const subById = useMemo(() => new Map(subs.map((s) => [s.id, s])), [subs]);
  const subsIn = useCallback((categoryId: number) => subs.filter((s) => s.categoryId === categoryId).sort(byOrder), [subs]);
  const procsIn = useCallback((subId: number) => procs.filter((p) => p.subcategoryId === subId).sort(byOrder), [procs]);

  // ── The board: categories › sub-categories › procedures ──────────────────
  // A search keeps a category, sub-category or procedure that matches (a
  // procedure by its HCPC code too), with everything under a matching parent.
  const board = useMemo(() => {
    const groups: BoardCategory[] = [];
    const subRows: Subcategory[] = [];
    const procRows: AdminProcedure[] = [];
    for (const c of sortedCats) {
      if (!showDeleted && !c.active) continue;
      const catHit = matches(c.name);
      let kept = false;
      for (const s of subsIn(c.id)) {
        if (!showDeleted && !s.active) continue;
        const subHit = catHit || matches(s.name);
        const rows = procsIn(s.id).filter((p) => (showDeleted || p.active) && (subHit || matches(p.name) || matches(p.hcpcCode)));
        if (narrowed && !subHit && rows.length === 0) continue;
        subRows.push(s);
        procRows.push(...rows);
        kept = true;
      }
      if (narrowed && !catHit && !kept) continue;
      groups.push({ id: c.id, title: nameOf(c, 'Category'), deleted: !c.active });
    }
    return { groups, subRows, procRows };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedCats, subsIn, procsIn, showDeleted, query]);

  // Parents a form may choose: the live ones.
  const liveCategories = useMemo(() => sortedCats.filter((c) => c.active), [sortedCats]);
  const liveSubOptions = useMemo(
    () =>
      liveCategories.flatMap((c) =>
        subsIn(c.id)
          .filter((s) => s.active)
          .map((s) => ({ id: s.id, label: `${nameOf(c, 'Category')} › ${nameOf(s, 'Sub-category')}` })),
      ),
    [liveCategories, subsIn],
  );

  // ── Board saves: a moved row and its new parent's list (the board rolls back when one rejects)
  async function reorderSubs(categoryId: number, ids: number[]) {
    await reorderProcedureRows('subcategories', ids, categoryId);
    setSubs((prev) => prev.map((s) => (ids.includes(s.id) ? { ...s, categoryId, order: ids.indexOf(s.id) + 1 } : s)));
  }

  async function reorderProcs(subcategoryId: number, ids: number[]) {
    await reorderProcedureRows('procedures', ids, subcategoryId);
    setProcs((prev) => prev.map((p) => (ids.includes(p.id) ? { ...p, subcategoryId, order: ids.indexOf(p.id) + 1 } : p)));
  }

  async function handleDelete() {
    if (!deleting) return;
    try {
      await deleteProcedureRow(deleting.level, deleting.id);
      toast.success(`${DELETE_COPY[deleting.level].noun[0].toUpperCase()}${DELETE_COPY[deleting.level].noun.slice(1)} deleted`);
      void load(true);
    } catch (err) {
      toast.error(getErrorMessage(err, `The ${DELETE_COPY[deleting.level].noun} could not be deleted.`));
    }
  }

  async function handleRestore(level: ProcedureLevel, id: number) {
    try {
      await restoreProcedureRow(level, id);
      toast.success('Restored');
      void load(true);
    } catch (err) {
      toast.error(getErrorMessage(err, 'It could not be restored.'));
    }
  }

  const saved = (close: () => void) => () => {
    close();
    void load(true);
  };

  // ── Rows ─────────────────────────────────────────────────────────────────
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

  // Inside the board's open / close button, so spans only. Edit is its pencil.
  function subRow(s: Subcategory) {
    const shown = board.procRows.filter((p) => p.subcategoryId === s.id).length;
    return (
      <span className="flex min-w-0 items-center gap-2" data-testid="subcategory-row">
        <span className={cn('truncate font-medium', s.active ? 'text-slate-800' : 'text-slate-400 line-through')}>{nameOf(s, 'Sub-category')}</span>
        <Badge variant="neutral" size="sm">{plural(shown, 'procedure', 'procedures')}</Badge>
        {s.documents.length > 0 && (
          <span className="hidden shrink-0 text-xs text-slate-500 md:inline">{plural(s.documents.length, 'document', 'documents')}</span>
        )}
      </span>
    );
  }

  function procRow(p: AdminProcedure) {
    const hours = Number(p.expNumHours) > 0 ? Number(p.expNumHours).toFixed(2) : '—';
    return (
      <button
        type="button"
        className={cn('flex w-full items-center gap-3 pr-2 text-left', !p.active && 'text-slate-400 line-through')}
        onClick={() => p.active && setProcModal({ proc: p, subcategoryId: p.subcategoryId })}
        disabled={!p.active}
        data-testid="procedure-row"
      >
        <span className="min-w-0 flex-1 truncate">{nameOf(p, 'Procedure')}</span>
        <span className="hidden w-20 shrink-0 font-mono text-slate-500 md:block">{p.hcpcCode || '—'}</span>
        <span className="w-16 shrink-0 text-right text-slate-500">{hours}</span>
        <span className="w-24 shrink-0 text-right">${p.feeToPatient}</span>
      </button>
    );
  }

  const COLUMNS = (
    <div className="flex items-center gap-3 pr-2">
      <span className="min-w-0 flex-1">Name</span>
      <span className="hidden w-20 shrink-0 md:block">HCPC</span>
      <span className="w-16 shrink-0 text-right">Hours</span>
      <span className="w-24 shrink-0 text-right">Service Fee</span>
    </div>
  );

  function restoreAction(level: ProcedureLevel, id: number, name: string) {
    return <AdminRowAction kind="restore" label={`Restore ${name}`} onClick={() => void handleRestore(level, id)} />;
  }

  const procedureBoard = (
    <NestedCategoryBoard<Subcategory, AdminProcedure>
      categories={board.groups}
      records={board.subRows}
      getRecordId={(s) => s.id}
      getRecordCategoryId={(s) => s.categoryId}
      getRecordTitle={(s) => nameOf(s, 'Sub-category')}
      childRecords={board.procRows}
      getChildId={(p) => p.id}
      getChildRecordId={(p) => p.subcategoryId}
      getChildTitle={(p) => nameOf(p, 'Procedure')}
      renderRecord={subRow}
      renderChild={procRow}
      columnHeader={COLUMNS}
      renderCategoryActions={(g) => {
        const c = catById.get(g.id);
        if (!c) return null;
        const name = nameOf(c, 'Category');
        if (!c.active) return restoreAction('categories', c.id, name);
        return (
          <>
            <AdminRowAction kind="add" title="Add sub-category" label={`Add sub-category to ${name}`} onClick={() => setSubModal({ sub: null, categoryId: c.id })} />
            <AdminRowAction kind="edit" label={`Edit ${name}`} onClick={() => setCategoryModal({ category: c })} viewWhenLocked />
            <AdminRowAction kind="delete" label={`Delete ${name}`} onClick={() => setDeleting({ level: 'categories', id: c.id, name })} />
          </>
        );
      }}
      renderRecordActions={(s) => {
        const name = nameOf(s, 'Sub-category');
        if (!s.active) return restoreAction('subcategories', s.id, name);
        return (
          <>
            <AdminRowAction kind="add" title="Add procedure" label={`Add procedure to ${name}`} onClick={() => setProcModal({ proc: null, subcategoryId: s.id })} />
            <AdminRowAction kind="edit" label={`Edit ${name}`} onClick={() => setSubModal({ sub: s, categoryId: s.categoryId })} viewWhenLocked />
            <AdminRowAction kind="delete" label={`Delete ${name}`} onClick={() => setDeleting({ level: 'subcategories', id: s.id, name })} />
          </>
        );
      }}
      renderChildActions={(p) => {
        const name = nameOf(p, 'Procedure');
        if (!p.active) return restoreAction('procedures', p.id, name);
        return (
          <>
            <AdminRowAction kind="edit" label={`Edit ${name}`} onClick={() => setProcModal({ proc: p, subcategoryId: p.subcategoryId })} />
            <AdminRowAction kind="delete" label={`Delete ${name}`} onClick={() => setDeleting({ level: 'procedures', id: p.id, name })} />
          </>
        );
      }}
      isRecordDeleted={(s) => !s.active}
      isChildDeleted={(p) => !p.active}
      onReorderRecords={reorderSubs}
      onReorderChildren={reorderProcs}
      onSaveError={(err) => toast.error(getErrorMessage(err, 'The new order could not be saved.'))}
      recordNoun={['sub-category', 'sub-categories']}
      dragDisabled={narrowed || showDeleted}
      expandAll={narrowed}
    />
  );

  const editingSub = subModal?.sub ? subById.get(subModal.sub.id) ?? subModal.sub : null;
  const subCategoryChoices = useMemo(() => {
    const current = editingSub ? catById.get(editingSub.categoryId) : undefined;
    return current && !current.active ? [...liveCategories, current] : liveCategories;
  }, [editingSub, catById, liveCategories]);
  const procSubChoices = useMemo(() => {
    const current = procModal?.proc ? subById.get(procModal.proc.subcategoryId) : undefined;
    if (!current || liveSubOptions.some((o) => o.id === current.id)) return liveSubOptions;
    const cat = catById.get(current.categoryId);
    return [...liveSubOptions, { id: current.id, label: `${cat ? nameOf(cat, 'Category') : 'No category'} › ${nameOf(current, 'Sub-category')}` }];
  }, [procModal, subById, catById, liveSubOptions]);

  return (
    <AdminBoardPage
      title="Procedures"
      subtitle="Categories, their sub-categories and the procedures under them. Quotes price from the procedures; plans, leads and tasks use the sub-categories."
      actions={
        <Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setCategoryModal({ category: null })}>
          Add category
        </Button>
      }
      toolbar={
        <AdminListToolbar
          className="mb-0"
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search categories, sub-categories and procedures…"
          searchLabel="Search procedures"
          showDeleted={showDeleted}
          onShowDeletedChange={setShowDeleted}
          showDeletedId="procedures-show-deleted"
        />
      }
      hint={boardDragHint({ searching: narrowed, filtering: false, showDeleted })}
      dialogs={
        <>
          {categoryModal && (
            <ProcedureCategoryModal
              key={categoryModal.category?.id ?? 'new'}
              category={categoryModal.category}
              onClose={() => setCategoryModal(null)}
              onSaved={saved(() => setCategoryModal(null))}
            />
          )}
          {subModal && (
            <ProcedureSubcategoryModal
              key={subModal.sub?.id ?? `new-${subModal.categoryId}`}
              subcategory={editingSub}
              defaultCategoryId={subModal.categoryId}
              categories={subCategoryChoices}
              documents={documents}
              documentsLoading={documentsLoading}
              hasIE={hasIE}
              onEditInquiry={(s) => setIeModal({ types: [1], ownerId: s.id, ownerName: nameOf(s, 'Sub-category') })}
              onClose={() => setSubModal(null)}
              onSaved={saved(() => setSubModal(null))}
            />
          )}
          {procModal && (
            <ProcedureModal
              key={procModal.proc?.id ?? `new-${procModal.subcategoryId}`}
              procedure={procModal.proc}
              defaultSubcategoryId={procModal.subcategoryId}
              subcategories={procSubChoices}
              maxHours={maxHours}
              hasIE={hasIE}
              onEditContent={(p) => setIeModal({ types: [2, 3, 4, 5, 6], ownerId: p.id, ownerName: nameOf(p, 'Procedure') })}
              onClose={() => setProcModal(null)}
              onSaved={saved(() => setProcModal(null))}
            />
          )}
          {ieModal && (
            <IeContentModal
              key={`${ieModal.types.join('')}-${ieModal.ownerId}`}
              types={ieModal.types}
              ownerId={ieModal.ownerId}
              ownerName={ieModal.ownerName}
              onClose={() => setIeModal(null)}
            />
          )}
          <ConfirmDialog
            isOpen={deleting !== null}
            onClose={() => setDeleting(null)}
            onConfirm={handleDelete}
            title={deleting ? `Delete ${DELETE_COPY[deleting.level].noun} "${deleting.name}"?` : ''}
            message={deleting ? `${DELETE_COPY[deleting.level].hidden} You can restore it from Show deleted.` : ''}
            confirmLabel={deleting ? `Delete ${DELETE_COPY[deleting.level].noun}` : 'Delete'}
            variant="danger"
          />
        </>
      }
    >
      {loading ? (
        <SpinnerOverlay label="Loading procedures…" />
      ) : board.groups.length === 0 ? (
        narrowed ? (
          <AdminListEmpty title="Nothing found" hint="Try adjusting your search." />
        ) : (
          <AdminListEmpty title="No categories yet" hint={locked ? undefined : 'Add a category, then add sub-categories and procedures to it.'} />
        )
      ) : (
        procedureBoard
      )}
    </AdminBoardPage>
  );
}

export default ProceduresAdmin;
